import {orderByStrAscending} from "nate-react-api-helpers";
import {PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {pdfTableStyle} from "../../../../misc/pdf/parts/Page";
import {GlazingSection} from "./GlazingSectionPage";

// all the glass in the submittal that shares a size, type and thickness, and how many pieces of it
// the project needs.
export type GlazingSummaryRow = {
    size: string;
    type: string;
    thickness: string;
    qty: number;
};

// roll the sections' glass up into distinct size/type/thickness lines. A section's table lists the
// glass for one elevation, so each of its rows is one piece per opening built to that elevation.
// `extra` is glass that has no section of its own (see GlazingCustomItem), rolled into the same
// lines.
export function summarizeGlass(sections: GlazingSection[], extra: GlazingSummaryRow[] = []): GlazingSummaryRow[] {
    const byKey = new Map<string, GlazingSummaryRow>();

    const add = (g: {size: string; type: string; thickness: string}, qty: number) => {
        const key = [g.size, g.type, g.thickness].join("|");
        const row = byKey.get(key);

        if(row) {
            row.qty += qty;
        } else {
            byKey.set(key, {size: g.size, type: g.type, thickness: g.thickness, qty: qty});
        }
    };

    sections.forEach(section => {
        const openingQty = section.openings.reduce((sum, o) => sum + o.qty, 0);

        const glass = [
            ...(section.panels || []).map(p => ({size: p.actualSize, type: p.kind, thickness: p.thickness})),
            ...(section.lites || []).map(l => ({size: l.glassSize, type: l.type, thickness: l.thickness})),
        ];

        glass.forEach(g => add(g, openingQty));
    });

    extra.forEach(e => add(e, e.qty));

    const rows = Array.from(byKey.values());
    orderByStrAscending(rows, r => [r.type, r.thickness, r.size].join("|"));
    return rows;
}

// the summary table without a page around it, so the purchase order PDF can print it under its
// own header.
export function GlazingSummaryTable(props: {
    rows: GlazingSummaryRow[];
}) {
    return (
        <table className={pdfTableStyle} style={{fontSize: "0.7rem"}}>
            <thead>
                <tr>
                    <th>Glass Size</th>
                    <th>Type</th>
                    <th>Thickness</th>
                    <th style={{textAlign: "right"}}>Qty</th>
                </tr>
            </thead>
            <tbody>
                {props.rows.map((r, index) => (
                    <tr key={index.toString()}>
                        <td>{r.size}</td>
                        <td>{r.type}</td>
                        <td>{r.thickness}</td>
                        <td style={{textAlign: "right"}}>{r.qty}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// the glass order for the whole submittal, ahead of the per-elevation sections.
export function GlazingSummaryPage(props: {
    rows: GlazingSummaryRow[];
    onReady(tf: boolean): void;
}) {
    return (
        <PageRenderProvider data={props.rows} onReady={props.onReady} debugKey="glazing-summary">
            {info =>
                <ContainerPage>
                    <TitleTextBlock title="GLASS SUMMARY">
                        <GlazingSummaryTable rows={info.rows} />
                    </TitleTextBlock>
                </ContainerPage>}
        </PageRenderProvider>
    );
}
